import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Stick-to-bottom scroll behaviour for the message list, matching the pattern
 * Claude/ChatGPT/Gemini use:
 *
 *  - While the user is at (or near) the bottom, new content auto-scrolls so the
 *    latest tokens stay in view as a turn streams.
 *  - The moment the user scrolls UP to read history, auto-scroll detaches and
 *    stays detached — we never yank them back down mid-read.
 *  - `atBottom` drives a floating "scroll to bottom" affordance; calling
 *    `scrollToBottom()` smoothly re-attaches.
 *
 * `contentKey` is any value that changes as content grows (message count,
 * streaming text length, status). When it changes and we're still attached,
 * we re-pin to the bottom.
 */
export function useStickToBottom(contentKey: unknown) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Whether auto-scroll is currently "attached". Starts true so a freshly
  // opened conversation lands at the newest message.
  const stuckRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);

  // Threshold (px) within which we still consider the user "at the bottom".
  // A few line-heights of slack so a tiny manual nudge doesn't detach.
  const NEAR_BOTTOM_PX = 80;

  const isNearBottom = useCallback((el: HTMLElement) => {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  // jsdom (and very old browsers) don't implement Element.scrollTo; fall back
  // to assigning scrollTop so this stays safe under test and degrades cleanly.
  const scrollElTo = (el: HTMLElement, top: number, behavior: ScrollBehavior) => {
    if (typeof el.scrollTo === "function") el.scrollTo({ top, behavior });
    else el.scrollTop = top;
  };

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    stuckRef.current = true;
    setAtBottom(true);
    scrollElTo(el, el.scrollHeight, behavior);
  }, []);

  // Track manual scrolling: detach when the user leaves the bottom, re-attach
  // when they return to it.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const near = isNearBottom(el);
    stuckRef.current = near;
    setAtBottom(near);
  }, [isNearBottom]);

  // When content grows and we're still attached, keep pinned to the bottom.
  // Uses "auto" (instant) so streaming doesn't fight a smooth animation.
  //
  // The re-pin runs inside a requestAnimationFrame rather than synchronously in
  // the effect: reading `scrollHeight` + writing scroll during the commit phase
  // forces a synchronous layout on every content tick (layout thrash). Deferring
  // to the next frame batches the read/write with the browser's own layout pass
  // and de-dupes rapid contentKey changes — only the latest pin lands. Coalesced
  // streaming dispatches already cap contentKey churn at ~frame rate; this keeps
  // the scroll write off the critical render path too.
  useEffect(() => {
    if (!stuckRef.current) return;
    const raf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(() => {
            if (!stuckRef.current) return; // user may have scrolled up since
            const el = scrollRef.current;
            if (el) scrollElTo(el, el.scrollHeight, "auto");
          })
        : null;
    if (raf == null) {
      // jsdom / no-rAF: keep the original synchronous behaviour so tests and
      // legacy environments still pin correctly.
      const el = scrollRef.current;
      if (el) scrollElTo(el, el.scrollHeight, "auto");
      return;
    }
    return () => cancelAnimationFrame(raf);
  }, [contentKey]);

  return { scrollRef, atBottom, scrollToBottom, onScroll };
}
